
import JSConfetti from 'js-confetti';
import { mixins, Options } from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { ShopifyCart } from '@/api/shopify/ShopifyCart';
import { TokenCart } from '@/api/shopify/TokenCart';
import { EligibleReward, TokenCartItem } from '@/api/shopify/types';
import { CAMPAIGN_CART_UPDATED } from '@/constants/events';
import CartWidgetMixin from '@/mixins/cartWidget';
import { RedemptionResult } from '../../api/models/redemption';
import { IToken } from '../../api/models/token';
import MReward from '../MReward.vue';
import MTokenAddToCart from '../MTokenAddToCart.vue';

@Options({
  components: {
    MReward,
    MTokenAddToCart
  },
  props: {
    goBack: {
      type: Function,
      required: false
    }
  }
})
export default class MEligibleRewards extends mixins(CartWidgetMixin) {
  goBack: () => void;

  showEligibleTab = true;
  eligibleRewards: EligibleReward[] = [];
  redeemedTokens: any[] = [];
  show = false;
  currentProductId: number;
  addingToCart = false;

  get claimText() {
    return this.innerClaimText ? this.innerClaimText : 'Add';
  }

  @Watch('state.eligibleRewards')
  eligibleRewardsWatcher(_value: EligibleReward[], _oldValue: EligibleReward[]) {
    this.updateTokens();
    this.updateRedeemedTokens();
  }

  walletLimitReached(reward: EligibleReward): boolean {
    if (this.state.campaign?.redemptionsPerAddress) {
      // Check to see if we have reached limit
      if (this.state.alreadyRedeemed.length >= this.state.campaign.redemptionsPerAddress) {
        return true;
      }
      // Check which current tokens are being used and check the limit
      const tokenCart = this.getTokenCart();
      const existingItems = tokenCart.findItemsWithReward(reward);
      // If reward was used, retain ability to use this item so they can modify the selection
      if (existingItems.length > 0) return false;
      // Check that all other rewards are not over limit already
      const campaignOtherTokenItems = tokenCart.items.filter(
        (x) =>
          x.campaignId === this.state.campaign?.id &&
          (x.token.network !== reward.token.network ||
            x.token.contractAddress !== reward.token.contractAddress ||
            x.token.tokenId !== reward.token.tokenId)
      );
      const uniqueOtherTokens = new Set<string>();
      for (const campaignOtherTokenItem of campaignOtherTokenItems) {
        const token = campaignOtherTokenItem.token;
        uniqueOtherTokens.add(`${token.network}-${token.contractAddress}-${token.tokenId}`);
      }
      if (uniqueOtherTokens.size >= this.state.campaign.redemptionsPerAddress) {
        return true;
      }
    }
    return false;
  }

  async updateRedeemedTokens() {
    if (this.productIdOverride) {
      this.currentProductId = this.productIdOverride;
    }
    if (!this.productIdOverride && !this.currentProductId) {
      const currentProduct = await this.state.ShopifyAPIClient.getCurrentProduct();
      this.currentProductId = currentProduct.id ? currentProduct.id : currentProduct.product.id;
    }
    const currentShop = await this.state.ShopifyAPIClient.getCurrentShop();

    const oauthToken = await this.getOAuth();
    const redemptions = await this.state.RedeemAPIClient.getRedemptionsForProduct(
      oauthToken,
      currentShop,
      this.currentProductId
    );

    this.redeemedTokens = redemptions;
  }

  getTokenCart() {
    const tokenCart = new TokenCart(this.state.storedShopifyCart, this.eligibleRewards);
    return tokenCart;
  }

  @Watch('state.alreadyRedeemed')
  alreadyRedeemedHandler(_value: any, _oldValue: any) {
    this.updateTokens();
  }

  @Watch('state.alreadyRedeemedByOthers')
  alreadyRedeemedByOthersHandler(_value: any, _oldValue: any) {
    this.updateTokens();
  }

  get redeemedTokensByOthers(): any[] {
    return this.state.alreadyRedeemedByOthers;
  }

  updateTokens() {
    const allEligibleRewards = this.state?.eligibleRewards;
    const eligibleRewards = [];

    // For all rewards
    for (let i = 0; i < allEligibleRewards?.length; i++) {
      for (let j = 0; j < allEligibleRewards[i].rules.length; j++) {
        eligibleRewards.push({
          limit: allEligibleRewards[i].rules[j].limit,
          token: allEligibleRewards[i].token,
          campaignId: allEligibleRewards[i].rules[j].campaignId,
          reward: allEligibleRewards[i].rules[j].rule
        });
      }
    }
    if (eligibleRewards?.length === 0) {
      this.showEligibleTab = false;
    }
    this.showEligibleTab = true;
    this.eligibleRewards = eligibleRewards;
  }

  async addToCart(reward: EligibleReward) {
    // Cannot invoke addToCart multiple times
    if (this.addingToCart) return;

    this.addingToCart = true;
    if (this.state.storedShopifyCart && this.state.storedShopifyCart.pendingDraftOrder) {
      alert(
        `You have a pending checkout. Please complete the checkout or clear your cart to add more token gated items.`
      );
      this.addingToCart = false;
      return;
    }

    const currentProduct = await this.state.ShopifyAPIClient.getCurrentProduct();
    const quantity = this.state.ShopifyAPIClient.getCurrentQuantity();
    const redemptionLimit = reward?.limit || 0;
    /**
     * For erc721 tokens on the whitelist this value is always 1
     * For erc1155s this value is:
     *    1 if you own one copy of the 1155 on the whitelist
     *    n if you own n copies of the 1155 on the whitelist
     */
    const remainingRedemptionsOnToken = reward?.token.remaining || 0;
    if (remainingRedemptionsOnToken === 1 && redemptionLimit && quantity > redemptionLimit) {
      alert('Too many selected');
      this.addingToCart = false;
      return;
    } else if (
      remainingRedemptionsOnToken > 1 &&
      redemptionLimit &&
      quantity > remainingRedemptionsOnToken
    ) {
      alert('Too many selected');
      this.addingToCart = false;
      return;
    }
    const currentVariantId =
      this.state.ShopifyAPIClient.getCurrentVariantId() || currentProduct.variants[0].id;
    let tokenCart = this.getTokenCart();

    const oauthToken = await this.getOAuth();

    // Parse out any custom properties needed
    let customProperties: { [key: string]: string } | undefined = undefined;
    if (this.productProperties) {
      customProperties = {};
      for (const productProperty of this.productProperties) {
        const propertyElement = document.getElementById(productProperty) as HTMLInputElement;
        if (propertyElement && propertyElement.value) {
          let propertyName = propertyElement.name;
          const propertyNameRegex = propertyName.match(/^properties\[(.*)\]$/);
          if (propertyNameRegex) {
            propertyName = propertyNameRegex[1];
          }
          customProperties[propertyName] = propertyElement.value.trim();
        }
      }
    }

    const newTokenCartItem: TokenCartItem = {
      campaignId: reward.campaignId,
      token: {
        network: reward.token.network,
        contractAddress: reward.token.contractAddress,
        tokenId: reward.token.tokenId,
        // Have to include ownerAddress and eip5131Address and eip5639Address
        ownerAddress: reward.token.ownerAddress,
        eip5639: reward.token.eip5639,
        eip5639Address: reward.token.eip5639Address,
        eip5131: reward.token.eip5131,
        eip5131Address: reward.token.eip5131Address
      },
      id: currentVariantId,
      product_id: currentProduct.id,
      product_handle: currentProduct.handle,
      variant_id: currentVariantId,
      quantity: quantity,
      ruleId: reward.reward.id,
      reward,
      customProperties
    };

    // Existing items sorted by quantity (desc)
    const existingTokenCartItems = tokenCart
      .findItemsWithReward(reward)
      .sort((a, b) => a.quantity - b.quantity);

    let existingVariant: TokenCartItem | undefined;

    // Determine items we have to remove
    const tokenCartItemsToRemove: TokenCartItem[] = [];
    let totalQuantity = quantity;
    for (const existingTokenCartItem of existingTokenCartItems) {
      if (existingTokenCartItem.id === currentVariantId) {
        // We have an existing match with a variant, we will be replacing this entry
        // The quantity is already counted
        existingVariant = existingTokenCartItem;
      } else if (existingTokenCartItem.quantity + totalQuantity <= redemptionLimit) {
        // We are not over limit, so we can add it
        totalQuantity += existingTokenCartItem.quantity;
      } else {
        // We are over limit, so we need to remove this item
        tokenCartItemsToRemove.push(existingTokenCartItem);
      }
    }

    const currentShop = await this.state.ShopifyAPIClient.getCurrentShop();
    const shopifyCart = new ShopifyCart(await this.state.ShopifyAPIClient.getBrowserCart());

    try {
      // Update the server side stored token cart object (tokenCart.items)
      if (existingVariant) {
        // We are replacing an item that's already in the cart, assign and replace the values
        Object.assign(existingVariant, newTokenCartItem);
      } else {
        // Push item into token cart
        tokenCart.items.push(newTokenCartItem);
      }
      if (tokenCartItemsToRemove.length > 0) {
        tokenCart.cart.items = tokenCart.cart.items.filter((x) => {
          for (const tokenCartItemToRemove of tokenCartItemsToRemove) {
            if (
              tokenCartItemToRemove.id === x.id &&
              tokenCartItemToRemove.campaignId === x.campaignId &&
              tokenCartItemToRemove.token.contractAddress === x.token.contractAddress &&
              tokenCartItemToRemove.token.tokenId === x.token.tokenId
            ) {
              return false;
            }
          }
          return true;
        });
      }

      // Remove items from the shopify cart if needed
      if (tokenCartItemsToRemove.length > 0) {
        await shopifyCart.removeTokenCartItems(tokenCartItemsToRemove, this.cartIconSection);
      }
      // Sync the updated item with the shopify cart
      const resp = await shopifyCart.syncTokenCartItem(newTokenCartItem, this.cartIconSection);

      if (resp && resp.sections && this.cartIconSection) {
        for (const cartIconSection of this.cartIconSection.split(',')) {
          const newCartIconBubble = resp.sections[cartIconSection];
          if (document.getElementById(cartIconSection) && newCartIconBubble) {
            document.getElementById(cartIconSection)!.innerHTML = newCartIconBubble;
          }
        }
      }
      // Save the token cart to the server
      tokenCart = await this.state.RedeemAPIClient.createOrUpdateCart(
        oauthToken,
        currentShop,
        tokenCart.cart
      );

      // Save modified cart locally in the store
      this.$store.commit(`${this.mmid}/setStoredShopifyCart`, tokenCart, { root: true });

      this.goBack();
      const jsConfetti = new JSConfetti();
      const emojis = this.addToCartEmojis || ['👕', '🧢'];

      if (!this.noEmojis) {
        jsConfetti.addConfetti({
          emojis
        });
      }

      if (this.reloadPageOnAddToCart) {
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
      if (this.redirectToCart) {
        setTimeout(() => {
          window.location.href = `${window.location.origin}/cart`;
        }, 2000);
      }

      // Dispatch cart updated event
      window.dispatchEvent(new CustomEvent(CAMPAIGN_CART_UPDATED));

      this.setActiveEligibleToken(reward.token);
    } catch (e: any) {
      // Error updating cart, so we need to re-sync the shopify cart with the current stored cart
      await this.syncShopifyCart(
        oauthToken!,
        currentShop,
        shopifyCart,
        this.state.storedShopifyCart
      );
      let message: any;
      if (e.message) message = e.message;
      if (message) {
        alert(
          `${message}\nPlease try adding your item again.\nIf you continue having a problem, try clearing your cart.`
        );
      } else {
        alert(
          `Problem adding to cart, please try again.\nIf you continue having a problem, try clearing your cart.`
        );
      }
    }
    this.addingToCart = false;
  }

  async setActiveEligibleToken(token: IToken) {
    this.$store.commit(`${this.mmid}/setActiveEligibleToken`, token, { root: true });
  }

  async setActiveRedemption(token: RedemptionResult) {
    this.$store.commit(`${this.mmid}/setActiveRedemption`, token, { root: true });
  }
}
